import { WabunkaError_Source } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';
import { WabunkaExperienceEventReservationType } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_reservation_type';
import { WabunkaReservation, WabunkaReservationCreateResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_reservation';

import { useSentry } from '@/composables/utils/useSentry';
import { useHangError } from '@/composables/utils/wabunka/useHangError';
import { useWabunkaPushGtmDatalayer } from '@/composables/utils/wabunka/useWabunkaPushGtmDatalayer';
import { useWabunkaStateFactory } from '@/composables/utils/wabunka/useWabunkaStateFacroty';
import { stateFactoryKey, type StateFactory } from '@/injectionKeys';
import type { EnExperienceViewModel } from '@/models/enExperience';
import { RESERVATION_TYPE_CHANGED_ERROR_CODE, type ValidationErrorObj } from '@/models/error';
import { createDefaultWabunkaExperienceEventInterpreterOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventInterpreterOption';
import { convertJstDayjsFromDate } from '@/utils/date';
import { getEnEmailRequiredAndFormatErrorMessage } from '@/utils/enValidator';
import { sleep } from '@/utils/sleep';
import { useValidationError } from '@/utils/typedError';
import { useAddGuestInfoGaEvent } from '@/wabunka-src/common/composables/events/useAddGuestInfoGaEvent';
import { useFeatureFlag } from '@/wabunka-src/common/composables/general/useFeatureFlag/useFeatureFlag';
import { useFetchToPublicWabunkaApi } from '@/wabunka-src/common/composables/query/useFetchToPublicWabunkaApi';
import { MAX_EMAIL_LENGTH, MAX_NAME_LENGTH, MAX_PHONE_NUMBER_LENGTH } from '@/wabunka-src/common/features/Reservation/core/requiredFields/fieldLimits';
import { buildReservationQuestionAnswers } from '@/wabunka-src/common/features/Reservation/core/reservationQuestionAnswers';
import { createDefaultReservationQuestionGaEventState } from '@/wabunka-src/common/features/Reservation/core/reservationQuestionGaEvents';
import { captureException } from '@/wabunka-src/common/utils';
import { sendGAWabunkaAddPaymentCardInfoEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/addPaymentCard';
import { sendGAWabunkaBeginCheckoutEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/beginCheckout';
import { buildRakutenAffiliateHeaders } from '@/wabunka-src/common/utils/headers';
import { CARD_PAYMENT_FAILED_MESSAGE } from '@/wabunka-src/constants/paymentErrorMessage';
import type { PaymentMethod } from '@/wabunka-src/constants/paymentMethod';
import {
  buildWabunkaReservationBase,
  buildWabunkaReservationFormFromCartItem,
  type ReservationQueryParams,
  createDefaultWabunkaReservationForm,
  type WabunkaReservationCartItem,
  type WabunkaReservationExperienceEventOptionForm,
  type WabunkaReservationForm,
  usePaymentClient,
  useGetPaymentMethod,
  buildReservationInputRequestV2,
} from '@/wabunka-src/features/Experience';
import { useCheckCodeUsability, buildWabunkaCode } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';

import type dayjs from 'dayjs';

const validateWabunkaReservationFormStep1 = (form: WabunkaReservationForm): ValidationErrorObj[] => {
  const errors: ValidationErrorObj[] = [];
  if (form.step1.selectedDate == null) {
    errors.push({
      fieldName: 'step1.selectedDate',
      message: 'Please select your first preferred date from the calendar.',
    });
  }
  if (form.step1.experienceEventId === '') {
    errors.push({
      fieldName: 'step1.experienceEventId',
      message: 'Please select your first preferred time',
    });
  }
  if (!form.step1.numOfParticipants) {
    if (form.experienceEvent.experience.chargeType === 'PER_GROUP') {
      errors.push({
        fieldName: 'step1.numOfParticipants',
        message: 'Please select number of participants in the group.',
      });
    } else {
      errors.push({
        fieldName: 'step1.numOfParticipants',
        message: 'Please select number of participants.',
      });
    }
  }
  return errors;
};

const validateEmail = (email: string, fieldName: string): ValidationErrorObj | null => {
  const message = getEnEmailRequiredAndFormatErrorMessage(email);
  if (message) return { fieldName, message };
  if (email.length > MAX_EMAIL_LENGTH) return { fieldName, message: `${MAX_EMAIL_LENGTH} characters or less` };
  return null;
};

const validateWabunkaReservationFormStep2 = (form: WabunkaReservationForm): ValidationErrorObj[] => {
  const errors: ValidationErrorObj[] = [];
  if (!form.step2.firstName)
    errors.push({
      fieldName: 'step2.firstName',
      message: 'Required fields',
    });
  else if (form.step2.firstName.length > MAX_NAME_LENGTH)
    errors.push({
      fieldName: 'step2.firstName',
      message: `${MAX_NAME_LENGTH} characters or less`,
    });
  if (!form.step2.lastName)
    errors.push({
      fieldName: 'step2.lastName',
      message: 'Required fields',
    });
  else if (form.step2.lastName.length > MAX_NAME_LENGTH)
    errors.push({
      fieldName: 'step2.lastName',
      message: `${MAX_NAME_LENGTH} characters or less`,
    });

  const emailError = validateEmail(form.step2.email, 'step2.email');
  if (emailError) errors.push(emailError);

  if (form.step2.shouldShowSomeoneElseForm) {
    // For someone else
    if (!form.step2.participantFirstName) errors.push({ fieldName: 'step2.participantFirstName', message: 'Required fields' });
    else if (form.step2.participantFirstName.length > MAX_NAME_LENGTH) errors.push({ fieldName: 'step2.participantFirstName', message: `${MAX_NAME_LENGTH} characters or less` });

    if (!form.step2.participantLastName) errors.push({ fieldName: 'step2.participantLastName', message: 'Required fields' });
    else if (form.step2.participantLastName.length > MAX_NAME_LENGTH) errors.push({ fieldName: 'step2.participantLastName', message: `${MAX_NAME_LENGTH} characters or less` });

    const forSomeoneElseEmailError = validateEmail(form.step2.forSomeoneElseEmail, 'step2.forSomeoneElseEmail');
    if (forSomeoneElseEmailError) errors.push(forSomeoneElseEmailError);

    if (!form.step2.forSomeoneElsePhoneNumber) errors.push({ fieldName: 'step2.forSomeoneElsePhoneNumber', message: 'Please enter phone number' });
    else if (form.step2.forSomeoneElsePhoneNumber.length > MAX_PHONE_NUMBER_LENGTH)
      errors.push({ fieldName: 'step2.forSomeoneElsePhoneNumber', message: `${MAX_PHONE_NUMBER_LENGTH} characters or less` });

    if (!form.step2.forSomeoneElseNationality) errors.push({ fieldName: 'step2.forSomeoneElseNationality', message: 'Please select your Nationality.' });
  } else {
    // For myself
    if (!form.step2.forMyselfPhoneNumber) errors.push({ fieldName: 'step2.forMyselfPhoneNumber', message: 'Please enter phone number' });
    else if (form.step2.forMyselfPhoneNumber.length > MAX_PHONE_NUMBER_LENGTH) errors.push({ fieldName: 'step2.forMyselfPhoneNumber', message: `${MAX_PHONE_NUMBER_LENGTH} characters or less` });

    if (!form.step2.forMyselfNationality) errors.push({ fieldName: 'step2.forMyselfNationality', message: 'Please select your Nationality.' });
  }

  if (!form.step2.experienceEventInterpreterOption.id)
    errors.push({
      fieldName: 'step2.experienceEventInterpreterOption',
      message: 'Please select your preferred language.',
    });

  // 予約質問の内、回答必須の質問に回答しているかを検証する
  const reservationQuestionAnswers = form.step2.reservationQuestionAnswers;
  if (reservationQuestionAnswers.length > 0) {
    for (let index = 0; index < reservationQuestionAnswers.length; index++) {
      const questionAnswer = reservationQuestionAnswers[index];
      if (!questionAnswer.isRequired) continue;

      if (questionAnswer.questionType === 'Radio' || questionAnswer.questionType === 'Checkbox') {
        // Radio / Checkbox: JSON配列をパースし、選択肢が0個の場合はエラー
        let selectedOptions: string[] = [];
        try {
          const parsed = questionAnswer.answer ? JSON.parse(questionAnswer.answer) : [];
          selectedOptions = Array.isArray(parsed) ? parsed : [];
        } catch {
          selectedOptions = [];
        }
        if (selectedOptions.length === 0) {
          errors.push({
            fieldName: `step2.reservationQuestionAnswers[${index}]`,
            message: 'Please select one or more options',
          });
        }
      } else if (!questionAnswer.answer) {
        // FreeText: 空文字の場合はエラー
        errors.push({
          fieldName: `step2.reservationQuestionAnswers[${index}]`,
          message: 'Required fields',
        });
      }
    }
  }
  return errors;
};

/**
 * Wabunka 予約作成Form用 Composable
 * formは、グローバルに変更できる
 */
export const useGlobalWabunkaReservationForm = () => {
  const isSending = ref(false);
  const stateFactory = inject(stateFactoryKey) as StateFactory;
  const form = stateFactory.create<WabunkaReservationForm>('wabunkaReservationForm', createDefaultWabunkaReservationForm());
  // isSending と違い Step2（カード入力）と Confirm（確定ボタン）で同じ状態を見る必要があるため、
  // composable ローカルの ref ではなくグローバルな form から導出する。
  // 文言ではなくフラグを見る。502 等で本文が JSON でないと文言は空になり得るが、そのときも導線は止めないといけない。
  const hasPaymentError = computed(() => form.value.step2.paymentFailed);
  // 予約確定時に種別が Request へ変わっており Pay.jp で確定できず、Fincode へ自動切替した直後を表すフラグ。
  // confirm ページ（onBook）で立て、遷移先の reserve ページ Step2（カード入力画面）で案内バナーを出すために使うため、
  // form と同じくグローバル state で保持する（プレーンな ref だとページ遷移で失われる）。
  const reservationTypeChangedToFincode = stateFactory.create<boolean>('wabunkaReservationTypeChangedToFincode', false);
  const reservationQuestionGaEventState = useWabunkaStateFactory('reservationQuestionGaEventState', createDefaultReservationQuestionGaEventState());
  const { errors, getErrorMessage, setErrors, hasError } = useValidationError();
  const { captureWabunkaException } = useSentry();
  const { startHangErrorTracker, clearHangErrorTracker } = useHangError();
  const { pushHashedEmailToGtmDatalayer } = useWabunkaPushGtmDatalayer();

  // 予約種別で決済プロバイダを振り分ける（B-1・要件2）。即時=Pay.jp / リクエスト=Fincode。
  // paymentMethod は予約種別（イベント選択後に確定）に応じて変わるため、usePaymentClient には
  // ref を渡し、トークン化・最低金額判定の各メソッド呼び出し時に最新の PSP を解決させる。
  const { paymentMethod } = useGetPaymentMethod(() => form.value.experienceEvent?.effectiveReservationType ?? form.value.experienceEvent?.reservationType);
  const { isBelowMinimumPaymentPrice, createToken } = usePaymentClient(paymentMethod);

  // Unleashフラグを最上位で取得（リアクティブな値として保持）
  const { featureFlag: isMultiPricingSwitchEnabled } = useFeatureFlag('wabunkaSwitchToMultiPricing');

  const reset = () => {
    form.value = createDefaultWabunkaReservationForm();
    reservationTypeChangedToFincode.value = false;
    // 予約が 1 件終わったら計測の記録も戻す。残すと同じ体験を続けて予約したときに 2 件目のイベントが 1 件も出ない
    reservationQuestionGaEventState.value = createDefaultReservationQuestionGaEventState();
  };

  const isPaymentSkip = (price: number) => {
    const isCodePaymentSkip = form.value.step2.code.benefitType === 'PAYMENT_SKIP';
    const isPublicPrice = form.value.experienceEvent.experience.isPublicPrice;
    const isBelowMinimum = isBelowMinimumPaymentPrice(price);

    return isCodePaymentSkip || isBelowMinimum || !isPublicPrice;
  };

  const onChangeTotalPriceIncludingTax = (totalPriceIncludingTax: number) => {
    form.value.step1.totalPriceIncludingTax = totalPriceIncludingTax;
  };

  // GAイベント用
  useAddGuestInfoGaEvent(form);

  const paymentErrorMessages = ref<string[]>([]);

  const initialUpdateFromExperienceEvent = (experienceEvent: WabunkaExperienceEventViewModel) => {
    // 新しい体験イベントの場合は通常の初期化処理
    form.value.experienceEvent = experienceEvent;
    form.value.step1.experienceEventId = experienceEvent.id;
    form.value.step1.numOfParticipants = experienceEvent.minGuestsPerReservation;
    form.value.step1.guestCounts = {};
    form.value.step2.experienceEventOptions = [];
    form.value.step2.experienceEventInterpreterOption = createDefaultWabunkaExperienceEventInterpreterOptionViewModel();
    form.value.step2.reservationQuestionAnswers = buildReservationQuestionAnswers(experienceEvent.experience.reservationQuestions);
  };
  const initialUpdateExperienceFromCMS = (experienceFromCMS: EnExperienceViewModel) => {
    form.value.experienceFromCMS = experienceFromCMS;
  };

  const isSameExperienceEventAsQuery = (query: ReservationQueryParams) => form.value.experienceEvent.id === query.expEvt;

  const setupFormWithQueryParams = (query: Ref<ReservationQueryParams>, experienceFromCMS: Ref<EnExperienceViewModel>, experienceEvent: Ref<WabunkaExperienceEventViewModel>) => {
    try {
      // 体験オプション情報の解析（3DS 復元が無く同じ体験イベントならフォーム → URLクエリパラメータ → _pendingRestoreOptions の優先順）
      let selectedExperienceEventExperienceOptionIdsWithCount: Record<string, number>;
      if (!form.value.step2._pendingRestoreOptions?.length && isSameExperienceEventAsQuery(query.value)) {
        // ブラウザの戻る・進む時: 予約ページで選んだ個数は URL に書き戻さないため、戻った先の URL ではなくフォームから復元
        selectedExperienceEventExperienceOptionIdsWithCount = Object.fromEntries(form.value.step2.experienceEventOptions.map((o) => [o.experienceEventOption.id, o.count]));
      } else if (query.value.ops) {
        // 再読み込み・URL 直開きなど、フォームがこの体験イベントを持たない時: URL クエリパラメータから復元
        selectedExperienceEventExperienceOptionIdsWithCount = query.value.ops.split(',').reduce(
          (acc, option) => {
            const [optionId, count] = option.split(':');
            acc[optionId] = count ? Number.parseInt(count, 10) : 0;
            return acc;
          },
          {} as Record<string, number>,
        );
      } else if (form.value.step2._pendingRestoreOptions?.length) {
        // 3DS 失敗後の restore 復元時: restore API レスポンスの options から復元
        selectedExperienceEventExperienceOptionIdsWithCount = Object.fromEntries(form.value.step2._pendingRestoreOptions.map((o) => [o.experienceEventOptionId, o.count]));
      } else {
        selectedExperienceEventExperienceOptionIdsWithCount = {};
      }
      // 一時保存した restore オプションをクリア
      form.value.step2._pendingRestoreOptions = undefined;
      const selectedExperienceEventExperienceOptionsWithCount: WabunkaReservationExperienceEventOptionForm[] = experienceEvent.value.experienceEventOptions
        .map((option) => ({
          experienceEventOption: option,
          count: selectedExperienceEventExperienceOptionIdsWithCount[option.id] ?? 0,
        }))
        .filter((option) => option.count > 0);
      form.value.experienceEvent = experienceEvent.value;
      form.value.experienceFromCMS = experienceFromCMS.value;
      form.value.step1.experienceEventId = experienceEvent.value.id;
      form.value.step1.selectedDate = convertJstDayjsFromDate(experienceEvent.value.startAt);

      // 予約質問の回答を保持（Backボタン時にユーザーが入力した内容を失わないため）
      // _pendingRestoreAnswers がある場合は ID 引きで適用（3DS 失敗後の restore 復元）
      const pendingRestoreMap = new Map((form.value.step2._pendingRestoreAnswers ?? []).map((a) => [a.questionId, a.answer]));
      form.value.step2.reservationQuestionAnswers = buildReservationQuestionAnswers(
        experienceEvent.value.experience.reservationQuestions,
        form.value.step2.reservationQuestionAnswers,
        pendingRestoreMap,
      );
      // 一時保存した restore 回答をクリア
      form.value.step2._pendingRestoreAnswers = undefined;

      // pplパラメータの解析（ゲストタイプ情報を含む場合とそうでない場合の両方に対応）
      if (experienceEvent.value.timeSlotId && query.value.ppl?.includes(':')) {
        // TimeSlotID対応体験でゲストタイプ情報を含む場合（例：b3b6bab1-aa72-4df1-8605-87d5be80b428:3）
        const guestCounts: Record<string, number> = {};
        let totalParticipants = 0;

        query.value.ppl.split(',').forEach((guestInfo) => {
          const [guestTypeId, count] = guestInfo.split(':');
          if (guestTypeId && count) {
            const countNum = Number.parseInt(count, 10);
            guestCounts[guestTypeId] = countNum;

            // isIncludedInHeadcountLimitがtrueのゲストタイプのみ合計に含める
            const guestTypeFee = experienceEvent.value.experienceEventFees?.find((fee) => fee.guestType.id === guestTypeId);
            if (guestTypeFee?.guestType.isIncludedInHeadcountLimit) {
              totalParticipants += countNum;
            }
          }
        });

        // 【重要】既存のゲストタイプ情報を保持しつつ、新しい情報で更新
        // Backボタン時にゲストタイプ入力フォームが正しく表示されるために必要
        // スプレッド演算子で既存の値を保持し、URLパラメータの値で上書きすることで
        // ユーザーが選択したゲストタイプの組み合わせを復元する
        form.value.step1.guestCounts = { ...form.value.step1.guestCounts, ...guestCounts };

        // 【重要】参加者数のフォールバック処理
        // Backボタン時にゲストタイプ入力フォームの合計人数表示が正しく動作するために必要
        // 1. totalParticipants: URLパラメータから計算した人数（最優先）
        // 2. form.value.step1.numOfParticipants: 既存フォームの値（ユーザーが入力済みの場合）
        // 3. experienceEvent.value.minGuestsPerReservation: 最小参加者数（デフォルト値）
        form.value.step1.numOfParticipants = totalParticipants || form.value.step1.numOfParticipants || experienceEvent.value.minGuestsPerReservation;
      } else {
        // 従来の数値のみの場合（TimeSlotIDが存在しない場合）
        form.value.step1.numOfParticipants = Number.parseInt(query.value.ppl, 10) || form.value.step1.numOfParticipants || experienceEvent.value.minGuestsPerReservation;
      }

      // 体験オプションの設定（Backボタン時にユーザーが選択したオプションを復元）
      form.value.step2.experienceEventOptions = selectedExperienceEventExperienceOptionsWithCount;

      // 【重要】通訳オプションの保持処理
      // Backボタン時にユーザーが選択した通訳言語を復元するために必要
      // URLパラメータ → 既存フォーム値 → デフォルト値の優先順位で設定
      form.value.step2.experienceEventInterpreterOption =
        experienceEvent.value.interpreterOptions.find((option) => option.id === query.value.int) ??
        form.value.step2.experienceEventInterpreterOption ??
        createDefaultWabunkaExperienceEventInterpreterOptionViewModel();

      // step1の情報が全て揃っていればOKのため、trueにする
      form.value.step1.isValidated = true;
    } catch (e) {
      captureWabunkaException(e as Error);
      return;
    }
  };

  const fillFormFromCartItem = (cartItem: WabunkaReservationCartItem) => {
    form.value = buildWabunkaReservationFormFromCartItem(cartItem);
  };

  // Step1の送信ボタンを押した時のイベントハンドラ
  const onClickStep1NextButton = async (onSuccess: () => void, totalPriceIncludingTax: number): Promise<void> => {
    // 連打防止
    if (isSending.value) {
      return;
    }
    isSending.value = true;

    // バリデーション
    errors.value = validateWabunkaReservationFormStep1(form.value);
    if (errors.value.length > 0) {
      isSending.value = false;
      form.value.step1.isValidated = false;
      return;
    }
    setErrors([]);
    isSending.value = false;
    form.value.step1.isValidated = true;

    // GAイベント送信
    sendGAWabunkaBeginCheckoutEvent(form.value.experienceEvent, form.value.step1, totalPriceIncludingTax);
    // 成功時の処理
    onSuccess();
  };

  // Step2の送信ボタンを押した時のイベントハンドラ
  const onClickStep2NextButton = async (onSuccess: () => void, isPaymentSkip: boolean): Promise<void> => {
    // 連打防止
    if (isSending.value) {
      return;
    }
    isSending.value = true;
    errors.value = validateWabunkaReservationFormStep2(form.value);
    // Code check
    if (form.value.step2.code.codeName && form.value.step2.code.codeName !== '') {
      const { data: code, error: codeCheckError } = await useCheckCodeUsability(form.value.step2.code.codeName, form.value.step2.email);
      if (!code.value || codeCheckError.value) {
        errors.value.push({
          fieldName: 'step2.code.codeName',
          message: codeCheckError.value?.getMessage() || '',
        });

        isSending.value = false;
        return;
      }

      form.value.step2.code = buildWabunkaCode(code.value);
    }

    // コード入力がない場合、フォームの値を初期化
    if (form.value.step2.code?.codeName === '') {
      form.value.step2.code.benefitType = undefined;
      form.value.step2.code.benefitValue = undefined;
    }

    if (errors.value.length > 0) {
      isSending.value = false;
      form.value.step2.isValidated = false;
      return;
    }
    if (!isPaymentSkip) {
      // createTokenでハングした際にエラー通知するための処理
      startHangErrorTracker();
      // payjp呼び出し
      const res = await createToken().finally(() => {
        clearHangErrorTracker();
      });

      if (res.error) {
        paymentErrorMessages.value = res.error.messages;
        isSending.value = false;
        form.value.step2.isValidated = false;
        return;
      }
      paymentErrorMessages.value = [];
      if (res.data) {
        form.value.step2.cardToken = res.data.cardToken;
        form.value.step2.cardLastFourDigits = res.data.cardLastFourDigits;
      }
    }

    await sleep(100);
    setErrors([]);
    isSending.value = false;
    // 新しいカードでトークン化できたので、前回の決済エラーによる確定ブロックを解除する。
    form.value.step2.paymentFailed = false;
    form.value.step2.isValidated = true;
    // カード再入力が完了したので、種別変更の案内バナー用フラグをクリアする。
    reservationTypeChangedToFincode.value = false;

    // フォームからEmailを取得し、GTMのdatalayerにハッシュ化したEmailをpushする
    await pushHashedEmailToGtmDatalayer(form.value.step2.email);

    // GAイベント送信
    sendGAWabunkaAddPaymentCardInfoEvent(form.value.experienceEvent);
    // 成功時の処理
    onSuccess();
  };
  const applyCode = async (codeName: string) => {
    // 連打防止
    if (isSending.value) {
      return;
    }
    isSending.value = true;
    // エラー文言配列を初期化
    setErrors([]);
    // バリデーション
    if (codeName == '') {
      errors.value.push({
        fieldName: 'step2.code.codeName',
        message: 'Required fields',
      });
      isSending.value = false;
      return;
    }
    // Code check
    const { data: code, error: codeCheckError } = await useCheckCodeUsability(codeName, form.value.step2.email);
    if (!code.value || codeCheckError.value) {
      errors.value.push({
        fieldName: 'step2.code.codeName',
        message: codeCheckError.value?.getMessage() || '',
      });

      isSending.value = false;
      return;
    }

    form.value.step2.code = buildWabunkaCode(code.value);
    if (errors.value.length > 0) {
      isSending.value = false;
      form.value.step2.isValidated = false;
      return;
    }
    form.value.step2.code.codeName = codeName;
    isSending.value = false;
  };

  const removeCode = () => {
    form.value.step2.code.codeName = '';
    form.value.step2.code.benefitType = undefined;
    form.value.step2.code.benefitValue = undefined;
  };

  // 決済情報に関しては、2回目以降の遷移時はリセットされるようにする
  const initPaymentInputs = () => {
    form.value.step2.cardToken = '';
    form.value.step2.cardLastFourDigits = '';
    form.value.step2.paymentFailed = false;
    // 種別変更→Fincode 自動切替の案内バナー用フラグもリセットする。reserve ページ mount 時にも呼ばれるため、
    // グローバル state に残ったフラグを別の予約フローへ持ち越さない。
    // （409 検知パスでは initPaymentInputs の後にフラグを立てるので、その場合の表示には影響しない）
    reservationTypeChangedToFincode.value = false;
  };

  /**
   * フォームの値からクエリパラメータを生成する
   */
  const buildQueryParamsByForm = (): ReservationQueryParams => {
    const experienceEventId = form.value.experienceEvent.id;
    const experienceOptions = form.value.step2.experienceEventOptions.map((opt) => `${opt.experienceEventOption.id}:${opt.count}`).join(',');
    const interpreterId = form.value.step2.experienceEventInterpreterOption.id;

    // pplパラメータの生成（ゲストタイプ情報を含む場合とそうでない場合の両方に対応）
    let pplParam: string;
    if (form.value.experienceEvent.timeSlotId && form.value.step1.guestCounts) {
      // ゲストタイプ情報を含む場合（例：b3b6bab1-aa72-4df1-8605-87d5be80b428:3,c7c8cbc2-bb83-4ef2-9716-98e6cf91c539:1）
      const guestTypeParams = Object.entries(form.value.step1.guestCounts)
        .filter(([, count]) => count > 0)
        .map(([guestTypeId, count]) => `${guestTypeId}:${count}`)
        .join(',');
      pplParam = guestTypeParams || form.value.step1.numOfParticipants.toString();
    } else {
      // 従来の数値のみの場合
      pplParam = form.value.step1.numOfParticipants.toString();
    }

    return { expEvt: experienceEventId, ops: experienceOptions, int: interpreterId, ppl: pplParam };
  };

  const onBook = async (onSuccess: () => void, paymentMethod: PaymentMethod, onBeforeThreeDSecureRedirect?: () => void): Promise<void> => {
    // 連打防止
    try {
      if (isSending.value) {
        return;
      }
      isSending.value = true;

      // マルチプライシングに対応した予約をするかの環境変数を取得
      const runtimeConfig = useRuntimeConfig();
      const isMultiPricePlanEnabled = runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED;

      // 旧予約エンドポイント(/reservations)を使用しようとしているかつUnleashフラグがONの場合
      // !isMultiPricePlanEnabled: これにより.envが反映されていないユーザーを対象にする
      if (!isMultiPricePlanEnabled && isMultiPricingSwitchEnabled.value) {
        captureException(new Error(`user has been blocked because of multi pricing release`));
        // アラートを表示
        alert('The application has been updated. We apologize for the inconvenience, but please restart the reservation process from the beginning.');

        // 体験詳細ページへリダイレクト（強制リフレッシュ）
        const experienceId = form.value.experienceFromCMS.id;
        window.location.href = `/experiences/${experienceId}/`;
        return;
      }

      // 楽天アフィリエイトヘッダーを構築
      const rakutenHeaders = buildRakutenAffiliateHeaders();

      const { data, error } = await useFetchToPublicWabunkaApi({
        path: ref('/reservations'),
        body: ref(buildReservationInputRequestV2(form.value, paymentMethod)),
        method: ref('POST'),
        options: { server: false },
        converter: (res: unknown) => WabunkaReservationCreateResponse.fromJSON(res),
        additionalHeaders: rakutenHeaders,
      });

      if (error.value != null) {
        // 予約確定時に種別が Request へ変わっており Pay.jp では確定できない場合、backend が 409 + code を返す。
        // Fincode へ自動切替し、連絡先・回答・日時・人数などの入力は保持したまま、カードだけ再入力してもらうため Step2 へ戻す。
        if (error.value.status === 409 && error.value.getCode() === RESERVATION_TYPE_CHANGED_ERROR_CODE) {
          // provider を Fincode に切替。effectiveReservationType=Request にすると、これを参照している
          // Step2 の paymentMethod（useGetPaymentMethod）が fincode を返し、カード入力フォームが Fincode に切り替わる。
          form.value.experienceEvent.effectiveReservationType = WabunkaExperienceEventReservationType.EXPERIENCE_EVENT_RESERVATION_TYPE_REQUEST;
          // Pay.jp のカード入力をクリア（Fincode で再入力してもらう）。
          initPaymentInputs();
          // カード未入力の状態に戻すため Step2 を未検証にする（confirm ドロワーを閉じ Step2 へ戻す）。
          form.value.step2.isValidated = false;
          // Step2 で案内バナーを出し、reserve ページ側で confirm ドロワーを閉じるためのフラグ。
          reservationTypeChangedToFincode.value = true;
          setErrors([]);
          isSending.value = false;
          return;
        }
        const errorSource = error.value.getSource();
        const isPaymentProviderError = errorSource === WabunkaError_Source.FINCODE || errorSource === WabunkaError_Source.PAYJP;
        setErrors([
          {
            fieldName: 'reservations',
            // 502 等でレスポンス本文から文言が取れないことがある。決済エラーは backend のマスク後文言と同じものを見せる
            message: error.value.getMessage() || (isPaymentProviderError ? CARD_PAYMENT_FAILED_MESSAGE : ''),
          },
        ]);

        if (isPaymentProviderError) {
          initPaymentInputs();
          form.value.step2.isValidated = false;
          form.value.step2.paymentFailed = true;
        }
        isSending.value = false;
        return;
      }

      // 3DS認証が必要な場合: redirect_url へリダイレクト（ページが切り替わるため以降の処理は不要）
      if (data.value?.requiresThreeDSecure) {
        isSending.value = false;
        // 意図的なリダイレクト前に beforeunload アラートを無効化
        onBeforeThreeDSecureRedirect?.();
        const rawRedirectUrl = data.value.redirectUrl;
        if (!rawRedirectUrl) {
          captureException(new Error('3DS redirect URL is missing'));
          return;
        }
        let url: URL;
        try {
          url = new URL(rawRedirectUrl);
        } catch {
          captureException(new Error(`invalid redirect url format: ${rawRedirectUrl}`));
          return;
        }
        // 3DS リダイレクト先として許可する PSP のホスト。
        // Pay.jp: pay.jp / payjp.com
        // Fincode: テスト環境の 3DS チャレンジは fincode 自ホスト（*.fincode.jp）だが、
        //   本番の実カード 3DS は Fincode(GMO-PG) の ACS フロント *.gmopg.jp
        //   （例: front.secure.gmopg.jp）へリダイレクトされるため両方を許可する。
        const allowedHosts = ['pay.jp', 'payjp.com', 'fincode.jp', 'gmopg.jp'];
        if (!allowedHosts.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`))) {
          captureException(new Error(`invalid redirect url: ${url.href}`));
          return;
        }
        window.location.href = url.href;
        return;
      }

      // 決済スキップ等で3DS不要な場合: 予約データを取り出して従来の完了フローへ
      // 予約完了後、カード情報はリセットする。但し、
      // 予約完了後のdone-reservationページへの遷移なため、検証済みとする(isValidated: true)
      initPaymentInputs();
      form.value.step2.isValidated = true;
      form.value.reservation = buildWabunkaReservationBase(data.value?.reservation ?? WabunkaReservation.create({}));
      setErrors([]);
      isSending.value = false;

      // 成功時の処理
      onSuccess();
    } catch (e) {
      clearHangErrorTracker();
      isSending.value = false;
      captureWabunkaException(e as Error);
      return;
    }
  };

  const onUpdateSelectedDateAndExpEventId = (selectedDate: dayjs.Dayjs | null, experienceEventId: string) => {
    form.value.step1.selectedDate = selectedDate;
    form.value.step1.experienceEventId = experienceEventId;
  };

  const onChangeExperienceEventId = (experienceEventId: string) => {
    form.value.step1.experienceEventId = experienceEventId;
  };

  return {
    form,
    reset,
    initPaymentInputs,
    initialUpdateFromExperienceEvent,
    onUpdateSelectedDateAndExpEventId,
    initialUpdateExperienceFromCMS,
    fillFormFromCartItem,
    getErrorMessage,
    onChangeExperienceEventId,
    errors,
    hasError,
    hasPaymentError,
    onClickStep1NextButton,
    onClickStep2NextButton,
    isSending,
    onBook,
    reservationTypeChangedToFincode,
    applyCode,
    removeCode,
    paymentErrorMessages,
    onChangeTotalPriceIncludingTax,
    setupFormWithQueryParams,
    isSameExperienceEventAsQuery,
    buildQueryParamsByForm,
    isPaymentSkip,
  };
};
